import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Globe2 } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { CORRIDORS, getCorridor } from "@/data/corridors";
import { useLeadModal } from "@/context/LeadModalContext";
import { scrollToCalculator } from "@/lib/notify";

export default function CorridorDetail() {
    const { slug } = useParams();
    const corridor = getCorridor(slug);
    const navigate = useNavigate();
    const { open: openLeadModal } = useLeadModal();

    useEffect(() => {
        document.title = corridor ? `TruoPay · India ↔ ${corridor.country}` : "TruoPay";
        window.scrollTo({ top: 0, behavior: "auto" });
    }, [corridor]);

    if (!corridor) {
        return (
            <main className="bg-white">
                <Navbar />
                <section className="bg-mesh-emerald text-white min-h-screen flex items-center px-4">
                    <div className="mx-auto max-w-xl text-center">
                        <h1 className="font-heading text-4xl font-extrabold">Corridor not found</h1>
                        <p className="mt-3 text-zinc-300">
                            We don't have details for "{slug}" yet.
                        </p>
                        <Link
                            to="/"
                            className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-bold text-zinc-950 glow-gold"
                        >
                            <ArrowLeft className="h-4 w-4" /> Back home
                        </Link>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <main data-testid={`corridor-page-${corridor.id}`} className="bg-white text-zinc-950">
            <Navbar />

            {/* Hero */}
            <section className="relative bg-mesh-emerald text-white pt-32 pb-20 md:pb-28 px-4 md:px-8 overflow-hidden">
                <div className="absolute inset-0 bg-grid-emerald opacity-50" aria-hidden />
                <div className="relative mx-auto max-w-7xl">
                    <button
                        type="button"
                        onClick={() => navigate("/#corridors")}
                        data-testid="corridor-back-btn"
                        className="inline-flex items-center gap-2 text-sm text-emerald-300 hover:text-white"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        All corridors
                    </button>
                    <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
                        <div className="max-w-3xl">
                            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-[11px] tracking-[0.25em] uppercase font-bold text-emerald-300">
                                <span aria-hidden>{corridor.flag}</span>
                                India ↔ {corridor.country}
                            </span>
                            <h1 className="font-heading mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
                                {corridor.tagline}
                            </h1>
                            <p className="mt-4 text-zinc-300 text-base sm:text-lg max-w-2xl">
                                Send and receive payments between India and {corridor.country} at
                                interbank rates with auto-compliance and audit-ready FIRA / FIRC.
                            </p>
                            <div className="mt-8 flex flex-col sm:flex-row gap-3">
                                <button
                                    type="button"
                                    onClick={() => openLeadModal("book_demo", "Book Demo")}
                                    data-testid="corridor-book-demo-btn"
                                    className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-gold px-6 text-base font-bold text-zinc-950 glow-gold hover:-translate-y-0.5 transition-all"
                                >
                                    Talk to corridor specialist
                                    <ArrowRight className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        navigate("/");
                                        setTimeout(scrollToCalculator, 120);
                                    }}
                                    data-testid="corridor-calc-btn"
                                    className="inline-flex h-12 items-center justify-center rounded-full border border-white/20 bg-white/5 px-6 text-base font-semibold text-white hover:bg-white/10"
                                >
                                    Run the FX calculator
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-3 sm:gap-4 w-full sm:w-auto">
                            {corridor.kpis.map((k) => (
                                <div
                                    key={k.k}
                                    className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-4 sm:p-5 min-w-[110px]"
                                >
                                    <div className="text-[10px] tracking-widest uppercase font-bold text-emerald-300">
                                        {k.k}
                                    </div>
                                    <div className="mt-1 font-mono text-xl sm:text-2xl font-extrabold">
                                        {k.v}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Details */}
            <section className="bg-white py-20 md:py-28 px-4 md:px-8">
                <div className="mx-auto max-w-7xl grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7">
                        <span className="text-xs tracking-[0.25em] uppercase font-bold text-emerald-700">
                            What's included
                        </span>
                        <h2 className="font-heading mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">
                            Everything you need to operate on the {corridor.country} corridor.
                        </h2>
                        <ul className="mt-8 space-y-4">
                            {corridor.benefits.map((b) => (
                                <li key={b} className="flex items-start gap-3 text-base text-zinc-700">
                                    <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100">
                                        <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                                    </span>
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-10 rounded-2xl border border-zinc-200 bg-zinc-50 p-6">
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div>
                                    <div className="text-xs tracking-widest uppercase font-bold text-zinc-500">
                                        Rails supported
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {corridor.rails.map((r) => (
                                            <span
                                                key={r}
                                                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-zinc-200 px-3 py-1 text-xs font-bold font-mono text-zinc-800"
                                            >
                                                <Globe2 className="h-3 w-3 text-emerald-600" />
                                                {r}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs tracking-widest uppercase font-bold text-zinc-500">
                                        Currencies
                                    </div>
                                    <div className="mt-2 font-mono text-xl font-bold text-zinc-950">
                                        {corridor.currencies.join(" · ")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <aside className="lg:col-span-5">
                        <div className="rounded-3xl bg-gradient-to-br from-[#04110D] via-[#063a2c] to-[#04110D] text-white p-6 sm:p-8 sticky top-28">
                            <div className="text-xs tracking-widest uppercase font-bold text-emerald-300">
                                Built for
                            </div>
                            <div className="mt-5 space-y-4">
                                {corridor.useCases.map((u) => (
                                    <div
                                        key={u.title}
                                        className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"
                                    >
                                        <div className="font-heading text-base font-bold">
                                            {u.title}
                                        </div>
                                        <div className="text-sm text-zinc-300 mt-1">{u.desc}</div>
                                    </div>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={() => openLeadModal("book_demo", "Book Demo")}
                                data-testid="corridor-side-cta"
                                className="mt-8 w-full inline-flex h-12 items-center justify-center gap-2 rounded-full bg-gold px-6 text-base font-bold text-zinc-950 glow-gold"
                            >
                                Get a personalised pricing
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                    </aside>
                </div>
            </section>

            {/* Other corridors */}
            <section className="bg-zinc-50 py-20 px-4 md:px-8">
                <div className="mx-auto max-w-7xl">
                    <h3 className="font-heading text-2xl sm:text-3xl font-extrabold">
                        Explore other corridors
                    </h3>
                    <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {CORRIDORS.filter((c) => c.id !== corridor.id).map((c) => (
                            <Link
                                key={c.id}
                                to={`/corridors/${c.slug}`}
                                data-testid={`corridor-cross-link-${c.id}`}
                                className="group rounded-2xl border border-zinc-200 bg-white p-6 hover:border-emerald-300 hover:shadow-md transition-all"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-2xl" aria-hidden>
                                        {c.flag}
                                    </span>
                                    <ArrowRight className="h-4 w-4 text-zinc-400 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all" />
                                </div>
                                <div className="mt-5 font-heading text-lg font-bold">
                                    India ↔ {c.country}
                                </div>
                                <div className="text-sm text-zinc-600 mt-1">
                                    {c.settlement} · {c.currencies.join("/")}
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}
